import { useEffect, useRef, useState } from "react";
import { ArrowRight, MessageCircle, Sparkles } from "lucide-react";
import client from "../lib/api";
import { detectCrisis, raiseCrisis } from "../lib/safety";
import iconMark from "../assets/brand/klareet-icon-white.png";
import { QuotaResetNote } from "../components/QuotaResetNote";

/**
 * Ask Klair. A free, unscored conversational surface. Every turn runs
 * through the distress rail server-side. No scoring on this surface.
 */
export default function AskKlairScreen() {
  const [messages, setMessages] = useState([
    { who: "klair", text: "Hi again. What are you thinking about right now?", animated: true },
  ]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [supportNotice, setSupportNotice] = useState(null);
  const [lastNote, setLastNote] = useState(null);
  const [quota, setQuota] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [starters, setStarters] = useState([]);
  const scrollRef = useRef(null);
  const convId = useRef((typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `c-${Date.now()}`);

  useEffect(() => {
    // KLA-026: the window is the real scroller; follow new messages only
    // when the reader is already near the bottom.
    const el = document.scrollingElement || document.documentElement;
    const nearBottom = window.innerHeight + window.scrollY >= el.scrollHeight - 320;
    if (nearBottom) window.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    client.get("/klair/quota").then((r) => setQuota(r?.data || null)).catch(() => setQuota(null));
  }, []);

  // Open with what matters: your goal, your last note, or what is next.
  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      client.get("/accountability/greeting"),
      client.get("/engagement/reflection"),
    ]).then(([g, r]) => {
      if (cancelled) return;
      const goal = g.status === "fulfilled" ? (g.value?.data?.greeting?.goal_text || "").trim() : "";
      const note = r.status === "fulfilled" ? (r.value?.data?.items?.[0]?.text || "").trim() : "";
      const options = [];
      if (goal) options.push(`pick up your goal, "${goal.slice(0, 70)}"`);
      if (note) options.push("talk about your last note");
      options.push("figure out what is next");
      const opener = `Hey, good to see you. Want to ${options.join(", ")}, or something else entirely?`;
      setMessages([{ who: "klair", text: opener, animated: true }]);
      if (note) setLastNote(note);
      const chips = [];
      if (goal) chips.push(`About my goal: ${goal.slice(0, 60)}`);
      chips.push("What should I focus on next?");
      chips.push("Help me think through a decision");
      chips.push("I just want to vent for a minute");
      setStarters(chips);
    });
    return () => { cancelled = true; };
  }, []);

  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    if (detectCrisis(t)) raiseCrisis("client");
    setMessages((m) => [...m, { who: "you", text: t }]);
    setText("");
    setBusy(true);
    setSupportNotice(null);
    try {
      const { data } = await client.post("/engagement/ask-klair", { text: t, conversation_id: convId.current });
      setMessages((m) => [...m, { who: "klair", text: data.reply, animated: true }]);
      if (data.support_notice) { setSupportNotice(data.support_notice); raiseCrisis("server"); }
      if (data.quota) setQuota((q) => ({ ...(q || {}), remaining: data.quota.remaining, exempt: data.quota.exempt, per_day: data.quota.per_day, ceiling: data.quota.ceiling }));
      if (data.blocked) setBlocked(true);
    } catch (_e) {
      setMessages((m) => [...m, { who: "klair", text: "That did not come through. Try again in a moment.", animated: true }]);
    } finally {
      setBusy(false);
    }
  };

  const showRemaining = quota && quota.exempt === false && typeof quota.remaining === "number";

  return (
    <div className="k-conv max-w-3xl mx-auto px-5 pt-8 pb-44 relative">
      <div className="flex items-center gap-3 mb-6" data-testid="askklair-header">
        <div className="relative rounded-[18px] p-[2.5px]" style={{ background: "var(--klareet-grad)" }}>
          <div className="relative w-12 h-12 rounded-[15.5px] flex items-center justify-center" style={{ background: "#1A202C" }}>
            <span className="k-ring-pulse" aria-hidden="true"></span>
            <img src={iconMark} alt="" aria-hidden="true" className="w-7 h-7" />
          </div>
        </div>
        <div>
          <div className="font-semibold" style={{ fontSize: "1.15rem" }}>Ask Klair</div>
          <div className="k-quiet !text-xs">free, unscored, private</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {showRemaining && (
            <span
              className="k-chip"
              style={quota.remaining <= 0 ? { background: "hsl(var(--destructive) / 0.12)", color: "hsl(var(--destructive))" } : undefined}
              title="Klair interactions left today. Unused ones stack up for you."
              data-testid="askklair-quota"
            >
              {quota.remaining} left today
            </span>
          )}
          <div className="k-chip"><MessageCircle size={12} /> chat</div>
        </div>
      </div>

      {lastNote && (
        <button
          type="button"
          onClick={() => setText(`About my note: "${lastNote.slice(0, 120)}" ... `)}
          className="w-full text-left rounded-xl p-3 mb-4 transition-all hover:-translate-y-0.5"
          style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))" }}
          data-testid="askklair-lastnote"
        >
          <div className="k-label mb-1">your last note</div>
          <div className="klareet-serif" style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.45 }}>{lastNote.slice(0, 160)}</div>
        </button>
      )}

      <div ref={scrollRef} className="space-y-4 min-h-[50vh]" data-testid="askklair-stream">
        {messages.map((m, i) => (
          <div key={i} className={m.who === "you" ? "flex justify-end" : "flex"}>
            <div
              className={`k-bubble ${m.who === "you" ? "k-bubble-you" : "k-bubble-klair"} ${m.animated ? "k-fade-in" : ""}`}
              data-testid={m.who === "klair" ? "askklair-bubble-klair" : "askklair-bubble-you"}
            >
              {m.text}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex">
            <div className="k-bubble k-bubble-klair k-fade-in" data-testid="askklair-thinking">
              <span className="k-think-dot" aria-hidden="true" />
            </div>
          </div>
        )}
      </div>

      {supportNotice && (
        <div className="k-warm-card p-4 mt-4 text-sm" data-testid="askklair-support">{supportNotice}</div>
      )}

      <div
        className="fixed bottom-0 left-0 lg:left-[246px] right-0 border-t"
        style={{
          borderColor: "hsl(var(--klareet-line-soft))",
          background: "hsl(var(--background) / 0.94)",
          backdropFilter: "blur(8px)",
        }}
      >
        <div className="max-w-3xl mx-auto px-5 py-4">
          {blocked && (
            <div className="k-warm-card p-3 mb-3 text-sm flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="askklair-blocked">
              <span>You have used your Klair conversations for today. They refresh tomorrow, and unused ones stack up for you.</span>
              <QuotaResetNote
                nextReset={quota?.next_reset}
                className="k-chip shrink-0"
                testId="askklair-reset-countdown"
              />
            </div>
          )}
          {!blocked && starters.length > 0 && messages.length <= 1 && (
            <div className="flex flex-wrap gap-2 mb-3" data-testid="askklair-starters">
              {starters.map((s, i) => (
                <button key={i} type="button" onClick={() => setText(s)} disabled={busy} className="k-suggest k-pop-in" style={{ animationDelay: `${i * 55}ms` }} data-testid={`askklair-starter-${i + 1}`}>
                  <Sparkles size={13} aria-hidden="true" /> {s}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              className="k-input resize-none klareet-serif"
              rows={2}
              placeholder={blocked ? "back tomorrow for more" : "anything you want to think out loud about"}
              value={text}
              disabled={blocked}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              data-testid="askklair-input"
            />
            <button onClick={send} disabled={busy || blocked || !text.trim()} className="k-btn k-btn-gradient !p-3.5 !rounded-2xl" data-testid="askklair-send" aria-label="send">
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
